/**
 * Life Event Timeline Analyzer Widget
 * Predicts when events are most likely to happen (not when to initiate them)
 */

import React, { useState, useMemo, useEffect } from 'react';
import { DateTime } from 'luxon';
import {
  Box,
  Typography,
  Select,
  Option,
  Button,
  Card,
  Chip,
  LinearProgress,
  Sheet,
  Alert,
  Divider,
  Stack,
} from '@mui/joy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { getCurrentDashaLevels } from '../../utils/vimshottari';
import {
  calculateNatalProbability,
  calculateDashaProbability,
  calculateTransitProbability,
  calculateCombinedProbability,
  type ProbabilityWindow,
  type NatalProbabilityInput,
  type LifeEventType,
} from '../../astro/probability/eventProbabilityEngine';
import ProbabilityTimelineChart from './ProbabilityTimelineChart';

interface LifeEventTimelineAnalyzerProps {
  astroData: any;
  d1HouseScores?: Record<number, number> | null;
  bhavaBalaRupas?: Record<number, number> | null;
  dashaContext?: any;
  utcDateTime?: string;
  vargaMap?: any;
  activeYogas?: any[];
  // v2 props for accuracy enhancements
  combust?: Partial<Record<string, boolean>>;
  debil?: Partial<Record<string, boolean>>;
  exalt?: Partial<Record<string, boolean>>;
  functionalNature?: Record<string, string>;
  houseInterpretations?: any[];
}

const ACTIVITY_OPTIONS: { value: LifeEventType; label: string; description: string }[] = [
  {
    value: 'business_start',
    label: '🏢 Career & Business',
    description: 'Job change, promotion, new business, or career breakthrough',
  },
  {
    value: 'property_purchase',
    label: '🏡 Property Acquisition',
    description: 'Buying house, land, or real estate',
  },
  {
    value: 'graha_pravesh',
    label: '🚪 Moving to New Home',
    description: 'Relocating or housewarming',
  },
  {
    value: 'vehicle_purchase',
    label: '🚗 Vehicle Purchase',
    description: 'Buying car, bike, or other vehicle',
  },
  {
    value: 'surgery',
    label: '🏥 Medical Procedure',
    description: 'Surgery or major medical treatment',
  },
  {
    value: 'ivf_planning',
    label: '👶 Conception Success',
    description: 'Natural or IVF conception',
  },
  {
    value: 'cesarean',
    label: '🤱 Childbirth',
    description: 'Delivery of child',
  },
  {
    value: 'investment',
    label: '📈 Investment',
    description: 'Financial investment or wealth creation',
  },
  {
    value: 'foreign_travel',
    label: '✈️ Foreign Travel',
    description: 'International journey or overseas relocation',
  },
  {
    value: 'marriage',
    label: '💍 Marriage',
    description: 'Marriage or long-term partnership',
  },
];

export default function LifeEventTimelineAnalyzer({
  astroData,
  d1HouseScores,
  bhavaBalaRupas,
  dashaContext,
  utcDateTime,
  vargaMap,
  activeYogas,
  combust,
  debil,
  exalt,
  functionalNature,
  houseInterpretations,
}: LifeEventTimelineAnalyzerProps) {
  const [selectedActivity, setSelectedActivity] = useState<LifeEventType>('business_start');
  const [analyzing, setAnalyzing] = useState(false);
  const [probabilityWindows, setProbabilityWindows] = useState<ProbabilityWindow[]>([]);
  const [yearsToAnalyze] = useState(1);

  const selectedActivityInfo = ACTIVITY_OPTIONS.find((a) => a.value === selectedActivity);

  // Debug: Log when probabilityWindows changes
  useEffect(() => {
    console.log('[LifeEventTimeline] probabilityWindows state updated:', probabilityWindows.length, 'windows');
  }, [probabilityWindows]);

  // Check if we have required data
  const canAnalyze = useMemo(() => {
    return !!(astroData && astroData.asc && dashaContext);
  }, [astroData, dashaContext]);

  // Calculate natal probability (once, doesn't change)
  const natalAnalysis = useMemo(() => {
    if (!canAnalyze) return null;

    const input: NatalProbabilityInput = {
      activity: selectedActivity,
      houseScores: d1HouseScores || undefined,
      bhavaBala: bhavaBalaRupas || undefined,
      planets: astroData.placements || astroData.planets,
      ascendantSign: astroData.asc.ascendantSignIndex,
      vargaData: vargaMap,
      yogas: activeYogas,
      functionalNature: functionalNature || undefined,
      debil: debil as Record<string, boolean> || undefined,
      exalt: exalt as Record<string, boolean> || undefined,
      houseInterpretations: houseInterpretations || undefined,
    };

    const result = calculateNatalProbability(input);
    console.log('[LifeEventTimeline] Natal analysis result:', {
      score: result.score,
      factorsCount: result.factors.length,
      factors: result.factors
    });
    return result;
  }, [canAnalyze, selectedActivity, d1HouseScores, bhavaBalaRupas, astroData, vargaMap, activeYogas]);

  // Analyze timeline
  const analyzeTimeline = () => {
    if (!canAnalyze || !natalAnalysis) {
      alert('Please ensure chart data is loaded');
      return;
    }

    console.log('[LifeEventTimeline] Starting analysis...', {
      activity: selectedActivity,
      hasAstroData: !!astroData,
      hasDashaContext: !!dashaContext,
      hasHouseScores: !!d1HouseScores,
      natalScore: natalAnalysis?.score,
    });

    setAnalyzing(true);
    setProbabilityWindows([]);

    setTimeout(() => {
      try {
        const windows: ProbabilityWindow[] = [];
        const today = new Date();
        const weeksToAnalyze = yearsToAnalyze * 52; // 5 years = 260 weeks
        const ascendantSign = astroData.asc.ascendantSignIndex;

        // Get all dasha periods for next 5 years
        const dashas = dashaContext.allPeriods || [];

        console.log('[LifeEventTimeline] Scanning', weeksToAnalyze, 'weeks, sampling every 4 weeks (monthly)');

        for (let week = 0; week < weeksToAnalyze; week += 4) {
          // Sample every 4 weeks (monthly)
          const startDate = new Date(today);
          startDate.setDate(today.getDate() + week * 7);

          const endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 28); // 4-week window

          // Calculate exact dasha periods for this future date
          let activeDashas: { MD: string; AD: string; PD: string; SD?: string } | null = null;
          
          if (utcDateTime && astroData.grahas?.Moon?.lon !== undefined) {
            try {
              const targetISO = DateTime.fromJSDate(startDate).toISO();
              const moonLon = astroData.grahas.Moon.lon; // Sidereal longitude
              
              const dashaLevels = getCurrentDashaLevels(utcDateTime, moonLon, targetISO!);
              
              if (dashaLevels) {
                activeDashas = {
                  MD: dashaLevels.md.lord,
                  AD: dashaLevels.ad?.lord || dashaLevels.md.lord,
                  PD: dashaLevels.pd?.lord || dashaLevels.ad?.lord || dashaLevels.md.lord,
                  SD: dashaLevels.sd?.lord,
                };
                
                // Log first window to verify dynamic dashas
                if (week === 0) {
                  console.log('[LifeEventTimeline] Dynamic dasha for', startDate.toISOString().split('T')[0], ':', activeDashas);
                }
              }
            } catch (error) {
              console.warn('[LifeEventTimeline] Error calculating dasha for', startDate.toISOString().split('T')[0], error);
            }
          }
          
          // Fallback to current dashas if calculation fails
          if (!activeDashas && dashaContext) {
            activeDashas = {
              MD: dashaContext.MD?.planet || 'Unknown',
              AD: dashaContext.AD?.planet || 'Unknown',
              PD: dashaContext.PD?.planet || 'Unknown',
              SD: dashaContext.SD?.planet || 'Unknown',
            };
          }
          
          if (!activeDashas) {
            console.warn('[LifeEventTimeline] No dasha info for', startDate.toISOString().split('T')[0]);
            continue;
          }
        // Calculate dasha probability (v2: pass placements + dignity maps)
        const dashaAnalysis = calculateDashaProbability(
          selectedActivity,
          activeDashas,
          astroData.placements || astroData.planets,
          ascendantSign,
          { debil: debil ?? undefined, exalt: exalt ?? undefined, combust: combust ?? undefined, functionalNature: functionalNature ?? undefined }
        );

        // Calculate transit probability (v2: pass natal moon sign for Sade Sati)
        const moonNatalSign = astroData.grahas?.Moon?.lon !== undefined
          ? Math.floor(((astroData.grahas.Moon.lon % 360) + 360) % 360 / 30) + 1
          : undefined;
        const transitAnalysis = calculateTransitProbability(
          selectedActivity,
          startDate,
          ascendantSign,
          undefined,
          moonNatalSign
        );

        // Calculate combined probability
        const combined = calculateCombinedProbability(
          natalAnalysis.score,
          dashaAnalysis.score,
          transitAnalysis.score
        );

        // Only include windows with Medium or higher probability (recalibrated threshold)
        if (combined.probability >= 25) {
          windows.push({
            startDate,
            endDate,
            probability: combined.probability,
            rating: combined.rating,
            natalScore: natalAnalysis.score,
            dashaScore: dashaAnalysis.score,
            transitScore: transitAnalysis.score,
            dashaPeriods: activeDashas,
            supportingFactors: [
              ...natalAnalysis.factors,
              ...dashaAnalysis.factors,
              ...transitAnalysis.factors,
            ].filter((f) => !f.includes('⚠') && !f.includes('Weak') && !f.includes('delays') && !f.includes('obstacles')),
            challengingFactors: [
              ...natalAnalysis.factors,
              ...dashaAnalysis.factors,
              ...transitAnalysis.factors,
            ].filter((f) => f.includes('⚠') || f.includes('Weak') || f.includes('delays') || f.includes('obstacles')),
            keyTransits: transitAnalysis.factors,
          });
        }
      }

      // Sort by probability (highest first)
      windows.sort((a, b) => b.probability - a.probability);

      console.log('[LifeEventTimeline] Analysis complete:', {
        totalWindows: windows.length,
        topProbability: windows[0]?.probability,
        avgProbability: windows.length > 0 ? Math.round(windows.reduce((sum, w) => sum + w.probability, 0) / windows.length) : 0,
      });

      console.log('[LifeEventTimeline] Setting state with', windows.length, 'windows');
      console.log('[LifeEventTimeline] Sample window:', windows[0]);

      setProbabilityWindows(windows);
      setAnalyzing(false);
      
      console.log('[LifeEventTimeline] State updated, analyzing=false');
    } catch (error) {
      console.error('[LifeEventTimeline] Analysis error:', error);
      alert('Error analyzing timeline: ' + (error as Error).message);
      setAnalyzing(false);
    }
    }, 500);
  };

  // Brand-palette color for probability rating
  const getRatingStyle = (rating: string): { hex: string; bg: string; border: string } => {
    switch (rating) {
      case 'Very High': return { hex: '#b83050', bg: 'rgba(184,48,80,0.10)',  border: 'rgba(184,48,80,0.30)' };
      case 'High':      return { hex: '#9b6fb5', bg: 'rgba(155,111,181,0.10)', border: 'rgba(155,111,181,0.30)' };
      case 'Medium':    return { hex: '#c8891a', bg: 'rgba(200,137,26,0.10)',  border: 'rgba(200,137,26,0.30)' };
      default:          return { hex: '#888888', bg: 'rgba(140,140,140,0.08)', border: 'rgba(140,140,140,0.25)' };
    }
  };

  // Format date range
  const formatDateRange = (start: Date, end: Date): string => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' };
    const startStr = start.toLocaleDateString('en-US', options);
    const endStr = end.toLocaleDateString('en-US', options);
    return startStr === endStr ? startStr : `${startStr} - ${endStr}`;
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Description - no duplicate header */}
      <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
        Analyze when events are most likely to happen naturally based on your chart, dasha periods,
        and transits (next {yearsToAnalyze} year).
      </Typography>

      {/* Activity Selector */}
      <Box sx={{ mb: 3 }}>
        <Select
          value={selectedActivity}
          onChange={(_, value) => value && setSelectedActivity(value)}
          sx={{ width: '100%' }}
        >
          {ACTIVITY_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
        {selectedActivityInfo && (
          <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary', fontStyle: 'italic' }}>
            {selectedActivityInfo.description}
          </Typography>
        )}
      </Box>

      {/* Natal Analysis Summary */}
      {natalAnalysis && (
        <Card variant="soft" sx={{ mb: 3, p: 2 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>
            📊 Your Chart's Potential (Natal Analysis)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography level="h2" sx={{ color: natalAnalysis.score >= 60 ? 'success.500' : 'warning.500' }}>
              {natalAnalysis.score}%
            </Typography>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                determinate
                value={natalAnalysis.score}
                color={natalAnalysis.score >= 60 ? 'success' : 'warning'}
                sx={{ height: 8 }}
              />
            </Box>
          </Box>
          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
            {natalAnalysis.score >= 70
              ? 'Strong natal potential - excellent chart support for this activity'
              : natalAnalysis.score >= 50
              ? 'Moderate natal potential - chart provides reasonable support'
              : 'Challenging natal configuration - requires favorable dasha/transits'}
          </Typography>
        </Card>
      )}

      {/* Analyze Button */}
      <Button
        onClick={analyzeTimeline}
        disabled={!canAnalyze || analyzing}
        loading={analyzing}
        size="lg"
        fullWidth
        startDecorator={<TrendingUpIcon />}
        sx={{ mb: 3 }}
      >
        {analyzing ? 'Analyzing Timeline...' : `Analyze Next ${yearsToAnalyze} Year`}
      </Button>

      {/* Results */}
      {probabilityWindows.length > 0 && (
        <Box>
          {/* Timeline Chart */}
          <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography level="title-md" sx={{ mb: 2, fontFamily: '"Cinzel", "Garamond", serif', color: '#7a1a2e', fontWeight: 700 }}>
              Probability Timeline
            </Typography>
            <ProbabilityTimelineChart
              windows={probabilityWindows}
              yearsToShow={yearsToAnalyze}
            />
            <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1.5, textAlign: 'center' }}>
              Visual representation of probability over time • Peaks indicate favorable periods
            </Typography>
          </Card>

          {/* Favorable Windows */}
          <Stack spacing={2}>
            {probabilityWindows.slice(0, 12).map((window, index) => (
              <Card key={index} variant="outlined" sx={{ p: 2 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box>
                    <Typography level="title-sm">{formatDateRange(window.startDate, window.endDate)}</Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {window.dashaPeriods.MD} MD • {window.dashaPeriods.AD} AD • {window.dashaPeriods.PD} PD
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography level="h3" sx={{ color: getRatingStyle(window.rating).hex }}>
                      {window.probability}%
                    </Typography>
                    <Box component="span" sx={{
                      display: 'inline-block',
                      px: 0.875, py: '2px', borderRadius: '999px',
                      fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.03em',
                      bgcolor: getRatingStyle(window.rating).bg,
                      color: getRatingStyle(window.rating).hex,
                      border: '1px solid',
                      borderColor: getRatingStyle(window.rating).border,
                    }}>
                      {window.rating}
                    </Box>
                  </Box>
                </Box>

                {/* Progress Bars - Stack on mobile, side-by-side on desktop */}
                <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', mb: 1.5 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1.5, sm: 2 }, 
                    fontSize: '0.75rem' 
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ mb: 0.5 }}>
                        Natal: {window.natalScore}%
                      </Typography>
                      <LinearProgress determinate value={window.natalScore} size="sm" />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ mb: 0.5 }}>
                        Dasha: {window.dashaScore}%
                      </Typography>
                      <LinearProgress determinate value={window.dashaScore} size="sm" />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ mb: 0.5 }}>
                        Transit: {window.transitScore}%
                      </Typography>
                      <LinearProgress determinate value={window.transitScore} size="sm" />
                    </Box>
                  </Box>
                </Sheet>

                {/* Supporting Factors - Show more including varga analysis */}
                {window.supportingFactors.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: 'success.700' }}>
                      ✓ Supporting Factors:
                    </Typography>
                    {window.supportingFactors.slice(0, 5).map((factor, i) => (
                      <Typography key={i} level="body-xs" sx={{ ml: 1, color: 'text.secondary' }}>
                        {factor}
                      </Typography>
                    ))}
                    {window.supportingFactors.length > 5 && (
                      <Typography level="body-xs" sx={{ ml: 1, color: 'text.tertiary', fontStyle: 'italic' }}>
                        +{window.supportingFactors.length - 5} more factors
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Challenging Factors - Show all including varga weaknesses */}
                {window.challengingFactors.length > 0 && (
                  <Box>
                    <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: 'warning.700' }}>
                      ⚠ Challenges:
                    </Typography>
                    {window.challengingFactors.slice(0, 4).map((factor, i) => (
                      <Typography key={i} level="body-xs" sx={{ ml: 1, color: 'text.secondary' }}>
                        {factor}
                      </Typography>
                    ))}
                    {window.challengingFactors.length > 4 && (
                      <Typography level="body-xs" sx={{ ml: 1, color: 'text.tertiary', fontStyle: 'italic' }}>
                        +{window.challengingFactors.length - 4} more challenges
                      </Typography>
                    )}
                  </Box>
                )}
              </Card>
            ))}
          </Stack>

          {probabilityWindows.length > 12 && (
            <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>
              Showing top 12 windows. Total {probabilityWindows.length} favorable periods found.
            </Typography>
          )}
        </Box>
      )}

      {/* No Results */}
      {!analyzing && probabilityWindows.length === 0 && (
        <Alert variant="soft" color="neutral" sx={{ textAlign: 'center' }}>
          <Typography level="body-md">Click analyze to see probability windows.</Typography>
        </Alert>
      )}

      {/* Educational Info */}
      <Card variant="soft" sx={{ mt: 3, p: 2 }}>
        <Typography level="title-sm" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlinedIcon fontSize="small" />
          How Probability is Calculated
        </Typography>
        <Typography level="body-sm" component="div">
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            <li>
              <strong>Natal Chart (40%)</strong>: Your birth chart's inherent support for the
              activity (house strengths, planet placements)
            </li>
            <li>
              <strong>Dasha Periods (40%)</strong>: Current planetary periods and which houses they
              activate (MD/AD set theme, PD enables events)
            </li>
            <li>
              <strong>Transits (20%)</strong>: Slow-moving planet positions (Jupiter, Saturn)
              providing temporary boosts or challenges
            </li>
          </ul>
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
          <strong>Note:</strong> This is <strong>predictive astrology</strong> (when events are
          likely to happen naturally). For choosing auspicious times to <em>initiate</em> actions,
          use the Personal Muhurta Advisor widget instead.
        </Typography>
      </Card>
    </Box>
  );
}
